import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { AnimatePresence, motion, useAnimationControls } from 'motion/react'
import { ArrowRight, Check, Eye, EyeOff } from 'lucide-react'
import { detailMessage } from '@/api/client'
import { useMeta } from '@/api/queries'
import { cn } from '@/lib/cn'
import { ease, spring } from '@/lib/motion'
import { AiOrb } from '@/components/ui/AiOrb'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { IconButton } from '@/components/ui/IconButton'
import { Spinner } from '@/components/ui/Spinner'
import { changePassword, signIn, signOut } from './local'

/**
 * Full-screen backdrop with a soft glow behind a glass card. The glow fades in once
 * and then stays still: anything moving behind the frosted card would force the
 * browser to re-blur it every frame.
 */
function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-canvas px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <motion.div
          className="size-[820px] shrink-0 rounded-full"
          // Soft by construction (no blur filter, which is costly to rasterise at this size).
          style={{
            background: [
              'radial-gradient(closest-side at 42% 46%, rgb(var(--ai-1) / 0.9), rgb(var(--ai-1) / 0.55) 20%, rgb(var(--ai-1) / 0.3) 40%, rgb(var(--ai-1) / 0.13) 60%, rgb(var(--ai-1) / 0.04) 80%, rgb(var(--ai-1) / 0))',
              'radial-gradient(closest-side at 60% 56%, rgb(var(--ai-3) / 0.9), rgb(var(--ai-3) / 0.55) 20%, rgb(var(--ai-3) / 0.3) 40%, rgb(var(--ai-3) / 0.13) 60%, rgb(var(--ai-3) / 0.04) 80%, rgb(var(--ai-3) / 0))',
            ].join(', '),
          }}
          initial={{ opacity: 0, transform: 'scale(0.85)' }}
          animate={{ opacity: 0.2, transform: 'scale(1)' }}
          transition={{ duration: 1.2, ease: ease.out }}
        />
      </div>
      <motion.div
        initial={{ opacity: 0, transform: 'translateY(16px)' }}
        animate={{ opacity: 1, transform: 'translateY(0px)' }}
        transition={{ duration: 0.55, ease: ease.out }}
        className="relative w-full max-w-[400px]"
      >
        {children}
      </motion.div>
    </div>
  )
}

function Header({ title, subtitle }: { title: string; subtitle: ReactNode }) {
  return (
    <div className="mb-7 flex flex-col items-center text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ ...spring.smooth, delay: 0.08 }}
      >
        <AiOrb className="size-14" />
      </motion.div>
      <h1 className="mt-5 text-[26px] font-semibold tracking-display text-ink">{title}</h1>
      <p className="mt-1.5 text-[15px] text-ink-muted">{subtitle}</p>
    </div>
  )
}

function FormError({ message }: { message: string | null }) {
  return (
    <AnimatePresence initial={false}>
      {message && (
        <motion.p
          key={message}
          role="alert"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: ease.out }}
          className="overflow-hidden text-center text-[13px] leading-5 text-danger"
        >
          <span className="block pt-1">{message}</span>
        </motion.p>
      )}
    </AnimatePresence>
  )
}

function RevealButton({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <IconButton label={shown ? 'Hide password' : 'Show password'} tooltip={false} size="sm" onClick={onToggle}>
      {shown ? <EyeOff /> : <Eye />}
    </IconButton>
  )
}

/** Card shake on a failed attempt, like the macOS login window. */
function useShake() {
  const controls = useAnimationControls()
  const shake = () =>
    void controls.start({ x: [0, -10, 9, -7, 5, -2, 0], transition: { duration: 0.45, ease: 'easeOut' } })
  return { controls, shake }
}

export function SignInScreen() {
  const meta = useMeta()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [shown, setShown] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { controls, shake } = useShake()
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    document.title = `Sign in · ${meta.assistant_name}`
  }, [meta.assistant_name])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await signIn(email, password)
    } catch (err) {
      setError(detailMessage(err))
      setPassword('')
      shake()
      passwordRef.current?.focus()
      setBusy(false)
    }
  }

  return (
    <AuthFrame>
      <Header title="Sign in" subtitle={<>to {meta.assistant_name}</>} />
      <motion.form
        animate={controls}
        onSubmit={submit}
        className="glass rounded-[28px] p-6 shadow-lift hairline"
        noValidate
      >
        <div className="space-y-4">
          <TextField
            label="Work email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <TextField
            ref={passwordRef}
            label="Password"
            type={shown ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={Boolean(error)}
            trailing={<RevealButton shown={shown} onToggle={() => setShown((v) => !v)} />}
          />
        </div>
        <FormError message={error} />
        <Button type="submit" className="group mt-5 w-full" disabled={busy || !email.trim() || !password}>
          {busy ? <Spinner className="size-4" /> : null}
          {busy ? 'Signing in…' : 'Sign in'}
          {!busy && (
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden />
          )}
        </Button>
      </motion.form>
      <p className="mt-6 text-center text-[13px] leading-5 text-ink-faint">
        Accounts are created by your administrator.
        <br />
        Forgot your password? Ask them to reset it.
      </p>
      <p className="mt-2 text-center text-[12px] text-ink-faint">{meta.org_name}</p>
    </AuthFrame>
  )
}

const RULES: { label: string; test: (p: string) => boolean }[] = [
  { label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { label: 'Upper- and lower-case letters', test: (p) => p.toLowerCase() !== p && p.toUpperCase() !== p },
  { label: 'At least one number', test: (p) => /\d/.test(p) },
]

/** Current + new password with a live checklist. Used on first sign-in and from the account menu. */
export function PasswordForm({
  onDone,
  submitLabel = 'Update password',
}: {
  onDone?: () => void
  submitLabel?: string
}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [shown, setShown] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { controls, shake } = useShake()

  const rulesOk = RULES.every((rule) => rule.test(next))
  const matches = next.length > 0 && next === confirm
  const ready = current.length > 0 && rulesOk && matches

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready || busy) return
    setBusy(true)
    setError(null)
    try {
      await changePassword(current, next)
      onDone?.()
    } catch (err) {
      setError(detailMessage(err))
      shake()
    } finally {
      setBusy(false)
    }
  }

  const reveal = <RevealButton shown={shown} onToggle={() => setShown((v) => !v)} />
  return (
    <motion.form animate={controls} onSubmit={submit} noValidate>
      <div className="space-y-4">
        <TextField
          label="Current password"
          type={shown ? 'text' : 'password'}
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          trailing={reveal}
        />
        <TextField
          label="New password"
          type={shown ? 'text' : 'password'}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <TextField
          label="Confirm new password"
          type={shown ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          aria-invalid={confirm.length > 0 && !matches}
          hint={confirm.length > 0 && !matches ? 'The passwords don’t match yet.' : undefined}
        />
      </div>
      <ul className="mt-4 space-y-1.5" aria-label="Password requirements">
        {RULES.map((rule) => {
          const ok = rule.test(next)
          return (
            <li key={rule.label} className="flex items-center gap-2 text-[13px]">
              <motion.span
                animate={{ scale: ok ? [1, 1.25, 1] : 1 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  'flex size-4 items-center justify-center rounded-full transition-colors duration-300',
                  ok ? 'bg-success text-white' : 'bg-ink/[0.08] text-transparent dark:bg-white/10',
                )}
                aria-hidden
              >
                <Check className="size-2.5" strokeWidth={3.5} />
              </motion.span>
              <span className={cn('transition-colors duration-300', ok ? 'text-ink' : 'text-ink-faint')}>
                {rule.label}
                <span className="sr-only">{ok ? ' (met)' : ' (not met)'}</span>
              </span>
            </li>
          )
        })}
      </ul>
      <FormError message={error} />
      <Button type="submit" className="mt-5 w-full" disabled={!ready || busy}>
        {busy && <Spinner className="size-4" />}
        {submitLabel}
      </Button>
    </motion.form>
  )
}

export function ChangePasswordScreen() {
  const meta = useMeta()

  useEffect(() => {
    document.title = `Choose a new password · ${meta.assistant_name}`
  }, [meta.assistant_name])

  return (
    <AuthFrame>
      <Header
        title="Choose a new password"
        subtitle={
          <>Welcome, {meta.user?.name.split(' ')[0] ?? 'there'}. Replace your temporary password to continue.</>
        }
      />
      <div className="glass rounded-[28px] p-6 shadow-lift hairline">
        <PasswordForm submitLabel="Save and continue" />
      </div>
      <p className="mt-6 text-center text-[13px] text-ink-faint">
        Not {meta.user?.email}?{' '}
        <button type="button" className="font-medium text-accent-text hover:underline" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
    </AuthFrame>
  )
}
